@if (loading) {
    <div class="flex justify-center p-8">Lade Saisondaten...</div>
}

@if (!loading) {
    <!-- Detail / hero section -->
    <div class="season-hero card flex flex-wrap items-center justify-between gap-6">
        <div>
            <div class="text-sm uppercase tracking-wider opacity-80">BPV Racing Liga</div>
            <div class="flex items-center gap-3 flex-wrap">
                <!-- The season picker IS the title — no separate select elsewhere. -->
                <p-select
                    class="season-title-select"
                    [options]="seasons"
                    optionLabel="name"
                    optionValue="season_id"
                    [ngModel]="selectedSeasonId"
                    (onChange)="onSeasonChange($event.value)"
                    [filter]="true"
                    filterBy="name"
                    placeholder="Season wählen"
                    appendTo="body"
                />
                @if (statusLabel) {
                    <p-tag [value]="statusLabel" [severity]="statusSeverity" />
                }
            </div>
        </div>

        <div class="season-facts">
            <div class="fact">
                <i class="pi pi-calendar"></i>
                <div>
                    <div class="fact-label">Start</div>
                    <div class="fact-value">{{ startDate ? (startDate | date: 'dd.MM.yyyy') : '–' }}</div>
                </div>
            </div>
            <div class="fact">
                <i class="pi pi-calendar-times"></i>
                <div>
                    <div class="fact-label">Ende</div>
                    <div class="fact-value">{{ endDate ? (endDate | date: 'dd.MM.yyyy') : '–' }}</div>
                </div>
            </div>
            <div class="fact">
                <i class="pi pi-map"></i>
                <div>
                    <div class="fact-label">Strecken</div>
                    <div class="fact-value">{{ circuitCount ?? '–' }}</div>
                </div>
            </div>
            <div class="fact">
                <i class="pi pi-sitemap"></i>
                <div>
                    <div class="fact-label">Gruppen</div>
                    <div class="fact-value">{{ groupCount ?? '–' }}</div>
                </div>
            </div>
        </div>
    </div>

    <!-- Statistics -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div class="card stat-card">
            <div class="stat-label">Renntage</div>
            <div class="stat-value">{{ raceDaysDone }} / {{ raceDaysTotal }}</div>
            <div class="stat-sub">gefahren / geplant</div>
            <i class="pi pi-flag stat-icon"></i>
        </div>
        <div class="card stat-card">
            <div class="stat-label">Teilnehmer</div>
            <div class="stat-value">{{ participantCount ?? '–' }}</div>
            <i class="pi pi-users stat-icon"></i>
        </div>
        @if (tableVersion === 'v1') {
            <div class="card stat-card">
                <div class="stat-label">{{ leaderLabel }} Gruppe A</div>
                <div class="stat-value text-2xl">{{ leaderNameGroupA ?? '–' }}</div>
                @if (leaderPointsGroupA != null) {
                    <div class="stat-sub">{{ leaderPointsGroupA }} Punkte</div>
                }
                <i class="pi pi-trophy stat-icon"></i>
            </div>
            <div class="card stat-card">
                <div class="stat-label">{{ leaderLabel }} Gruppe B</div>
                <div class="stat-value text-2xl">{{ leaderNameGroupB ?? '–' }}</div>
                @if (leaderPointsGroupB != null) {
                    <div class="stat-sub">{{ leaderPointsGroupB }} Punkte</div>
                }
                <i class="pi pi-trophy stat-icon"></i>
            </div>
        } @else {
            <div class="card stat-card">
                <div class="stat-label">{{ leaderLabel }}</div>
                <div class="stat-value text-2xl">{{ leaderName ?? '–' }}</div>
                @if (leaderPoints != null) {
                    <div class="stat-sub">{{ leaderPoints }} Punkte</div>
                }
                <i class="pi pi-trophy stat-icon"></i>
            </div>
            <div class="card stat-card">
                <div class="stat-label">Meiste Tagessiege</div>
                <div class="stat-value text-2xl">{{ mostWinsNames ?? '–' }}</div>
                @if (mostWinsCount > 0) {
                    <div class="stat-sub">{{ mostWinsCount }} {{ mostWinsCount === 1 ? 'Sieg' : 'Siege' }}</div>
                }
                <i class="pi pi-star-fill stat-icon"></i>
            </div>
        }
    </div>

    <!-- Race calendar, in the same look as the home dashboard -->
    <div class="card">
        <app-raceDaysOverview-widget [season_id]="selectedSeasonId" [seasonName]="season?.name ?? null" [color_season]="true" />
    </div>

    <!-- Rules overview, collapsed by default like the Regelwerk accordion -->
    @if (rules.length > 0) {
        <div class="card">
            <p-accordion [value]="[]" [multiple]="true">
                <p-accordion-panel value="0">
                    <p-accordion-header><div class="text-xl text-orange-500">Regeln</div></p-accordion-header>
                    <p-accordion-content>
                        <div class="flex justify-end mb-2">
                            <a routerLink="/pages/ruleset" class="font-semibold no-underline hover:underline text-orange-500">
                                Zum Regelwerk <i class="pi pi-arrow-right text-sm"></i>
                            </a>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                            @for (rule of rules; track rule.rule_id) {
                                <div class="rule-row">
                                    <span class="rule-label">{{ rule.label }}</span>
                                    <span class="rule-value">{{ rule.value }}</span>
                                </div>
                            }
                        </div>
                    </p-accordion-content>
                </p-accordion-panel>
            </p-accordion>
        </div>
    }

    <!-- League table + races -->
    @switch (tableVersion) {
        @case ('v1') {
            <div class="card">
                <div class="font-semibold text-xl mb-4">Gruppe A</div>
                <div class="font-semibold text-l mb-4">Ligatabelle</div>
                <app-leageTable [season]="selectedSeasonId!" [group]="groupA" />
                <div class="mt-5">
                    <app-leagueRaceTable [season]="selectedSeasonId!" [group]="groupA" />
                </div>
            </div>
            <div class="card">
                <div class="font-semibold text-xl mb-4">Gruppe B</div>
                <div class="font-semibold text-l mb-4">Ligatabelle</div>
                <app-leageTable [season]="selectedSeasonId!" [group]="groupB" />
                <div class="mt-5">
                    <app-leagueRaceTable [season]="selectedSeasonId!" [group]="groupB" />
                </div>
            </div>
        }
        @case ('v2') {
            <div class="card">
                <div class="font-semibold text-xl mb-4">Ligatabelle</div>
                <app-leageTableV2 [season]="selectedSeasonId!" [group]="1" />
                <div class="mt-5">
                    <app-leagueRaceTableV2 [season]="selectedSeasonId!" />
                </div>
            </div>
        }
        @case ('v3') {
            <app-qualification-table [season]="selectedSeasonId!" [seasonName]="season?.name ?? null" />
            <div class="card">
                <div class="font-semibold text-xl mb-4">Ligatabelle</div>
                <app-leageTableV3 [season]="selectedSeasonId!" [seasonName]="season?.name ?? null" [group]="1" />
                <div class="mt-5">
                    <app-leagueRaceTableV2 [season]="selectedSeasonId!" />
                </div>
            </div>
        }
    }
}
